import { useEffect, useRef, useMemo } from 'react';
import {
  generatePath,
  Outlet,
  useParams,
  NavLink,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  Filters,
  useFilters,
  IconExpand,
  SchemaFilterType,
  PermissionEnum,
  useGetList,
  usePrevious,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetRateRequestQuery } from './store/services';
import {
  rateRequestListColumns,
  rateRequestListSchema,
  rateRequestListAllSchema,
  rateRequestListFiltersSchema,
  rateRequestListColumnsLabels,
  rateRequestListAllColumnsLabels,
  rateRequestListAllFiltersSchema,
} from './variables/list';
import formatRateRequestList from './helpers/format-rate-request';
import { RateRequest } from './types';

import ROUTES from '../../routes';

import cn from './rate-request.module.css';

const RateRequestList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    rateRequest: {
      list: { rateRequest, pagination },
    },
    exchangeDictionaries: { currencies, activePermissions },
  } = useAppSelector((state) => state);

  const { currency } = useParams();
  const { search } = useLocation();

  const { filters, sort, onFilterChange } = useQueryState(
    currency === 'all' ? rateRequestListAllSchema : rateRequestListSchema,
  );

  const [getList, { isLoading, isFetching }] = useLazyGetRateRequestQuery();

  const prevCurrency = usePrevious(currency);

  const formattedData = useMemo(() => {
    const data = formatRateRequestList(
      rateRequest as RateRequest[],
      currency as string,
      currencies,
    );

    return data;
  }, [rateRequest, sort, filters, currencies]);

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<RateRequest>({
      getList,
      onFilterChange,
      onSortChange: () => {},
      schema:
        currency === 'all' ? rateRequestListAllSchema : rateRequestListSchema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema:
      currency === 'all'
        ? rateRequestListAllFiltersSchema
        : rateRequestListFiltersSchema,
    filters,
    onFilterChange: () => {},
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (filters && !rateRequest) {
      let currentFilters = filters;

      if (currency !== 'all') {
        currentFilters = {
          ...currentFilters,
          exchangeCurrencyToId: {
            value: [currency as string],
            type: SchemaFilterType.STRING,
          },
        };
      }

      if (!rateRequest) {
        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    } else if (prevCurrency && currency !== prevCurrency) {
      const nextFilters: Filters = {};

      if (currency !== 'all') {
        nextFilters.exchangeCurrencyFromId = {
          value: [currency as string],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search.replace('?', ''));

        if (Object.keys(parsed).length) {
          Object.keys(parsed).forEach((item) => {
            nextFilters[item] = {
              value: [parsed[item] as string],
              type: SchemaFilterType.STRING,
            };
          });
        }
      }

      onFilter(nextFilters);
    }
  }, [filters, sort, rateRequest, currency, prevCurrency, search]);

  useEffect(() => {
    if (currencies) {
      let formattedCurrencies = currencies.map((item) => ({
        name: `${item.name} (${item.description})`,
        value: item.id,
      }));

      if (currency !== 'all') {
        formattedCurrencies = formattedCurrencies.filter(
          (item) => item.value !== currency,
        );
      }

      setValues('exchangeCurrencyToId', formattedCurrencies);
      setValues('exchangeCurrencyFromId', formattedCurrencies);
    }
  }, [currencies, currency]);

  useEffect(() => {
    let title = 'FX Rate request';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  const addUrl = generatePath(ROUTES.RATE_REQUEST_ADD, {
    currency: currency as string,
  });

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.rateRequestLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={rateRequestListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={
          currency === 'all'
            ? rateRequestListAllColumnsLabels
            : rateRequestListColumnsLabels
        }
        onFilterChange={(filtersValue) => {
          const nextFilters = { ...filtersValue };

          if (currency !== 'all') {
            nextFilters.exchangeCurrencyFromId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          onFilter(nextFilters);
        }}
        error={errorList}
        filtersComponent={filtersComponent}
        isLoading={isFetching}
        tableBodyClassName={`${!rateRequest?.length ? cn.emptyTable : ''} ${
          isFetching ? cn.tableLoading : ''
        }`}
        tableTheadClassName={cn.rateTableHead}
        handleSubmit={handleSubmit}
        pagination={pagination}
        take="20"
        setPage={(data) => {
          const nextFilters = {
            ...filters,
          };

          if (currency !== 'all') {
            nextFilters.exchangeCurrencyToId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: nextFilters });
        }}
        topContent={
          <div className={cn.autoSettingsTop}>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_CREATE_ACTION,
            ) && (
              <NavLink to={`${addUrl}${search || ''}`} className={cn.addRate}>
                <span>
                  <IconExpand />
                </span>
                <span>Add Pair</span>
              </NavLink>
            )}
          </div>
        }
        clickable
      />
    );
  }

  return (
    <div className={cn.rateRequestWrapper} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default RateRequestList;
